import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bpClient } from '../api/l1';
import { useAuth } from '../context/AuthContext';
import monetedgeLogo from '../assets/MonetEdge.svg';

interface TosStatus {
  accepted: boolean;
  version: string;
  content_url: string | null;
}

const getTosStatus = () => bpClient.get<TosStatus>('/blueprint-runtime/iam/tos/status');
const acceptTos = (version: string) => bpClient.post<TosStatus>('/blueprint-runtime/iam/tos/accept', { version });

// Blocking gate rendered by RequireAuth in place of the app shell until the signed-in
// user (local-admin or CIAM tenant, uniformly -- see iam_tos.py) has accepted the
// current TOS_VERSION. Returns null (renders nothing extra) once accepted.
//
// Fixed 2026-08-09: this used to also skip blocking whenever data.version === 'unset',
// matching iam_tos.py's old "unset means unconfigured, don't force acceptance" default --
// but that made the gate silently never fire on any deployment that hadn't explicitly set
// TOS_VERSION (this one included), indistinguishable from "already accepted." Direct
// instruction: always show/require it. iam_tos.py now defaults TOS_VERSION to a real
// version string instead of "unset", so this frontend check is removed rather than kept
// as now-unreachable dead code.
export function useTosGate() {
  const { isAuthenticated } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['tos-status'],
    queryFn: () => getTosStatus().then((r) => r.data),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
  const blocked = isAuthenticated && !isLoading && !!data && !data.accepted;
  return { status: data, loading: isAuthenticated && isLoading, blocked };
}

export function TosGateModal({ status }: { status: TosStatus }) {
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAccept() {
    setError(null);
    setSubmitting(true);
    try {
      await acceptTos(status.version);
      await queryClient.invalidateQueries({ queryKey: ['tos-status'] });
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Could not record acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, backgroundColor: 'rgba(15, 23, 42, 0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div className="card" style={{ maxWidth: 480, width: '100%', padding: 24 }}>
        <div className="flex justify-center mb-4">
          <img src={monetedgeLogo} alt="MonetEdge" style={{ height: 30, width: 'auto', objectFit: 'contain' }} />
        </div>
        <h1 className="section-title">Terms of Service</h1>
        <p className="text-xs text-slate-400 mt-1 mb-4">
          Please review and accept the current Terms of Service (version {status.version}) to continue.
        </p>

        {status.content_url ? (
          <a
            href={status.content_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 underline block mb-4"
          >
            Read the full Terms of Service
          </a>
        ) : (
          <p className="text-xs text-amber-600 mb-4">
            The Terms of Service document isn't configured for this deployment yet -- contact your administrator.
          </p>
        )}

        <label className="flex items-start gap-2 text-xs text-slate-600 mb-4">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5"
          />
          <span>I have read and accept the Terms of Service.</span>
        </label>

        {error && <p className="text-xs text-red-600 mb-3">{error}</p>}

        <button
          className="btn-primary w-full justify-center"
          disabled={!checked || submitting}
          onClick={handleAccept}
        >
          {submitting ? 'Recording…' : 'Accept & Continue'}
        </button>
      </div>
    </div>
  );
}
